import { FC, useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { MatrixGrid } from "./MatrixGrid";
import { MatrixCellContainer } from "./MatrixCellContainer";
import { MatrixMessage } from "./MatrixMessage";
import { ThresholdControls } from "./ThresholdControls";
import type { Band, MatrixThresholds } from "@/lib/scoring/matrix/bands";
import {
  profileScoringAxes,
  transformFilterScoring,
} from "@/lib/scoring/scoringUtils";
import type { ScoringProfile } from "@/lib/scoring/types";
import type { OpportunityGeneratorFilter } from "@/generated/company-search";

interface ScoringMatrixProps {
  /** The focused profile (drives the matrix scores); null shows the empty state. */
  profile: ScoringProfile | null;
  /** Color of the focused profile (matches its pill). */
  dotColor: string;
  /** Segment filters (already stripped of intent UI-only fields). */
  baseFilters?: OpportunityGeneratorFilter;
  localizeInstalls: boolean;
  thresholds: MatrixThresholds;
  onThresholdsChange: (t: MatrixThresholds) => void;
  onCellClick: (fit: Band, intent: Band) => void;
}

export const ScoringMatrix: FC<ScoringMatrixProps> = ({
  profile,
  dotColor,
  baseFilters,
  localizeInstalls,
  thresholds,
  onThresholdsChange,
  onCellClick,
}) => {
  const scoringProfiles = useMemo(
    () => (profile ? transformFilterScoring([profile]) : []),
    [profile]
  );

  const [loadingCells, setLoadingCells] = useState<Record<string, boolean>>({});
  const handleCellLoading = useCallback((key: string, loading: boolean) => {
    setLoadingCells((prev) =>
      prev[key] === loading ? prev : { ...prev, [key]: loading }
    );
  }, []);
  const anyLoading = Object.values(loadingCells).some(Boolean);

  if (!profile) {
    return (
      <MatrixMessage>
        Apply and focus a scoring profile to see the matrix.
      </MatrixMessage>
    );
  }

  // The Fit×Intent matrix only makes sense when the focused profile scores on
  // both axes.
  const axes = profileScoringAxes(profile);
  if (!axes.hasFit || !axes.hasIntent) {
    const missing = axes.hasFit ? "intent" : "fit";
    return (
      <MatrixMessage>
        <strong>{profile.name}</strong> scores only{" "}
        {axes.hasFit ? "fit" : "intent"}. The matrix needs both Fit and Intent —
        focus a profile scored on both, or add {missing} criteria to this
        profile.
      </MatrixMessage>
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={{ height: "100%", minHeight: 0, maxWidth: 1180 }}
      data-testid="scoring-matrix"
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flexShrink: 0 }}
      >
        <Box
          sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: dotColor }}
        />
        <Typography variant="body2" color="text.secondary">
          Scoring by <strong>{profile.name}</strong>
        </Typography>
        {anyLoading && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ ml: 1 }}
          >
            <CircularProgress size={14} thickness={5} />
            <Typography variant="caption" color="text.secondary">
              Calculating distribution… this can take a moment
            </Typography>
          </Stack>
        )}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <MatrixGrid
          renderCell={(fit, intent) => (
            <MatrixCellContainer
              baseFilters={baseFilters}
              scoringProfiles={scoringProfiles}
              localizeInstalls={localizeInstalls}
              fit={fit}
              intent={intent}
              thresholds={thresholds}
              onClick={() => onCellClick(fit, intent)}
              onLoadingChange={(loading) =>
                handleCellLoading(`${fit}-${intent}`, loading)
              }
            />
          )}
        />
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <ThresholdControls value={thresholds} onChange={onThresholdsChange} />
      </Box>
    </Stack>
  );
};
